import {
  Alert,
  AlertTitle,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Tab,
  Tabs,
  TextField,
  Toolbar,
  Typography
} from "@mui/material";
import React, { useCallback, useContext, useMemo } from "react";
import { useDispatch } from "react-redux";
import { useState } from "react";
import { Box } from "@mui/system";
import { get, post } from "../helpers/request";
import { CustomizerContext } from "../context/CustomizerContext";
import { getDataJson } from "../helpers/GetDataJson";
import * as Y from 'yjs'
import { red } from "@mui/material/colors";
import ReactDiffViewer from 'react-diff-viewer'
import styled from "styled-components";
import { AbcOutlined, PreviewOutlined, VerticalSplitOutlined } from "@mui/icons-material";
import { useEffect } from "react";
import cssbeautify from 'cssbeautify';
import { reloadSites } from "../helpers/common_requests";

const dialogStyle = { "& .MuiDialog-paper": { maxWidth: "90%", maxHeight: "90%" } }
const boxButtonsStyle = { m: 1, position: 'relative' }
const circularProgressStyle = {
  color: red[500],
  position: 'absolute',
  top: '50%',
  left: '50%',
  marginTop: '-12px',
  marginLeft: '-12px',
}

const convertToFilesData = (entries) => {
  const languages = Object.keys(entries).filter(key => key.startsWith('data-'))
  const dataLangs = languages.reduce((memo, language) => {
    memo[language + '.js'] = `window.__data = ${JSON.stringify(entries[language])}`
    return memo
  }, {})

  const files = {
    "data.js": `window.__data = ${JSON.stringify(entries.data)}`,
    "general.json": `${JSON.stringify(entries.general)}`,
    "theme.css": entries.theme?.toString() || "",
    "data_sellers.json": `${JSON.stringify(entries.datasellers)}`,
    "theme-sellers.css": entries['themesellers']?.toString() || "",
    ...dataLangs
  }

  return Object.keys(files).map(name => ({ name, body: files[name] }))
}

const formatFiles = (files) => {
  return Object.keys(files).reduce((memo, fileKey) => {
    if (fileKey.startsWith('data-') || ['data', 'datasellers', 'general'].includes(fileKey)) memo[fileKey] = JSON.stringify(files[fileKey] || {}, null, 2)
    else if (['theme', 'themesellers'].includes(fileKey)) memo[fileKey] = cssbeautify(files[fileKey] || '', { indent: '  ' })
    else memo[fileKey] = files[fileKey] || ''
    return memo
  }, {})
}

const DeployStagingModal = ({ isOpen, onClose, selectedVersion, currentSite }) => {
  const { document } = useContext(CustomizerContext);
  const [description, setDescription] = useState("");
  const [deploying, setDeploying] = useState(false);
  const [error, setError] = useState(false);
  const [step, setStep] = useState('preview')
  const [newFormattedFiles, setNewFormattedFiles] = useState()
  const [oldFormattedFiles, setOldFormattedFiles] = useState()
  const dispatch = useDispatch();

  const newFiles = useMemo(() => {
    if (!isOpen) return null
    const data = document.getMap("data");
    const general = document.getMap("general");
    const theme = document.get("theme.draft", Y.XmlText);
    const themeSellers = document.get("themeSellers.draft", Y.XmlText);
    const dataSellers = document.getMap("dataSellers");
    const languages = document.getArray("languages");

    const dataJson = data.toJSON();
    const generalJson = general.toJSON();
    const dataSellersJson = dataSellers.toJSON();

    const dataLangs = languages.toJSON().reduce((memo, lang) => {
      const langJson = getDataJson({ data, currentData: `data-${lang.code}`, document })
      memo[`data-${lang.code}`] = langJson
      return memo
    }, {});

    return {
      "data": dataJson,
      "general": generalJson,
      "theme": theme?.toString() || "",
      "datasellers": dataSellersJson,
      "themesellers": themeSellers?.toString() || "",
      ...dataLangs
    }
  }, [document, isOpen])

  const handleClose = useCallback(() => {
    setError(false)
    setDeploying(false)
    if (onClose) onClose()
  }, [onClose]);

  const handleDeploy = useCallback(() => {
    if (document) {
      const files = convertToFilesData(newFiles)
      setDeploying(true);
      setError(false);
      post(
        `${process.env.REACT_APP_API_URL}/internal/admin/v3/templates/deploy`,
        {
          template_version_id: selectedVersion?.template_version_id || 1,
          site_id: currentSite?.child_site?.site_id,
          description,
          files,
        }
      )
        .then(() => {
          dispatch({
            type: "show-notification",
            content: "Successful deploy!",
            severity: "success",
          });
          setDescription("");
          onClose();
        })
        .then(() => reloadSites({ dispatch, current_site_id: currentSite?.child_site?.site_id }))
        .catch((error) => {
          setError(error);
        })
        .finally(() => {
          setDeploying(false)
          setStep('preview')
          setOldFormattedFiles(null)
          setNewFormattedFiles(null)
        });
    }
  }, [currentSite, document, newFiles, onClose, selectedVersion, description, dispatch]);

  const handleDescriptionChange = useCallback((event) => {
    setDescription(event.currentTarget.value);
  }, []);

  const handleNext = useCallback(() => setStep('description'), [])
  const handlePrevious = useCallback(() => setStep('preview'), [])

  useEffect(() => {
    if (!isOpen) return
    setStep('preview')
    get(`${process.env.REACT_APP_API_URL}/internal/admin/v2/file_loader/${currentSite?.site_id}/${currentSite?.child_site?.current_version}/data-theme-datasellers-themesellers-general`)
      .then(settings => {
        setOldFormattedFiles(formatFiles(settings))
        setNewFormattedFiles(formatFiles(newFiles))
      })
      .catch(error => {
        console.error(error)
      })
  }, [currentSite, isOpen, newFiles])

  return (
    <Dialog
      sx={dialogStyle}
      maxWidth="xs"
      open={isOpen}
    >
      <DialogTitle>Deploy to STAGING</DialogTitle>
      {step === 'preview' && oldFormattedFiles && newFormattedFiles &&
        <DialogContent dividers>
          <DiffView oldFiles={oldFormattedFiles} newFiles={newFormattedFiles} />
        </DialogContent>
      }
      {step === 'preview' && !oldFormattedFiles && !newFormattedFiles &&
        <CircularProgress size={24} sx={circularProgressStyle} />      
      }
      {step === 'description' &&
        <DialogContent dividers>
          <DialogContentText>
            Type a description for this version
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            id="description"
            label="Description"
            type="email"
            fullWidth
            variant="standard"
            onChange={handleDescriptionChange}
          />
          <p>
            Domain:{" "}
            <a
              href={`https://${currentSite?.child_site?.domain}`}
              target="_blank"
              rel="noreferrer"
            >
              {currentSite?.child_site?.domain}
            </a>
          </p>
          {error && (
            <Alert severity="error">
              <AlertTitle>Deploy Failed</AlertTitle>
              {error.message}
            </Alert>
          )}
        </DialogContent>
      }
      <DialogActions>
        <Button autoFocus onClick={handleClose}>
          Cancel
        </Button>
        {step === 'preview' &&
          <Button 
            variant="contained" 
            color="info"
            onClick={handleNext}
          >
            Next
          </Button>      
        }
        {step === 'description' &&
          <Button 
            onClick={handlePrevious}
          >
            Previous
          </Button>      
        }
        {step === 'description' &&
          <Box sx={boxButtonsStyle}>
            <Button
              variant="contained"
              color="error"
              onClick={handleDeploy}
              disabled={deploying}
            >
              Deploy
            </Button>
            {deploying && (
              <CircularProgress size={24} sx={circularProgressStyle} />
            )}
          </Box>
        }
      </DialogActions>
    </Dialog>
  )
}

const DiffContainer = styled.div`
  display: flex;
  flex-direction: column;
`

const ToolbarContainer = styled.div`
  display: flex;
  align-items: center;
  & > p {
    flex-grow: 1;
  }
`

const DiffView = ({ oldFiles, newFiles }) => {
  const [splitView, setSplitView] = useState(false)
  const [diffOnly, setDiffOnly] = useState(true)
  const [wordDiff, setWordDiff] = useState(true)
  const [tab, setTab] = useState()

  const fileKeys = useMemo(() => {
    const unique = [...new Set([...Object.keys(oldFiles), ...Object.keys(newFiles)])]
    return unique.filter(key => (oldFiles[key] || '') !== (newFiles[key] || ''))
  }, [oldFiles, newFiles])

  const handleSplitView = useCallback(() => setSplitView(!splitView), [splitView])
  const handleDiffOnly = useCallback(() => setDiffOnly(!diffOnly), [diffOnly])
  const handleWordDiff = useCallback(() => setWordDiff(!wordDiff), [wordDiff])

  const handleChangeTab = useCallback((_event, newTab) => setTab(newTab), [])

  useEffect(() => {
    if (fileKeys && fileKeys.length > 0) setTab(fileKeys[0])
  }, [fileKeys])

  return (
    <DiffContainer>
      <ToolbarContainer>
        <Typography>List of changes</Typography>
        <Toolbar variant="dense">
          <IconButton
            size="small"
            color={splitView ? 'info' : 'default'}
            onClick={handleSplitView}
          >
            <VerticalSplitOutlined />
          </IconButton>
          <IconButton
            size="small"
            color={diffOnly ? 'info' : 'default'}
            onClick={handleDiffOnly}
          >
            <PreviewOutlined />
          </IconButton>
          <IconButton
            size="small"
            color={wordDiff ? 'info' : 'default'}
            onClick={handleWordDiff}
          >
            <AbcOutlined />
          </IconButton>
        </Toolbar>
      </ToolbarContainer>
      {tab &&
        <>
          <Tabs variant="scrollable" scrollButtons="auto" value={tab} onChange={handleChangeTab}>
            {fileKeys.map(key => 
              <Tab key={key} label={key} value={key} />
            )}
          </Tabs>
          <ReactDiffViewer
            oldValue={oldFiles[tab] || ''}
            newValue={newFiles[tab] || ''}
            splitView={splitView}
            showDiffOnly={diffOnly}
            disableWordDiff={!wordDiff}
          />
        </>
      }
    </DiffContainer>
  )
}

export default DeployStagingModal